import { useEffect, useRef } from "react";
import { onboardingIdFor } from '@/lib/speedRun';
import { onboardingEnabled, startIntentGuide, whenFirstVisitDecided } from "@/lib/onboarding";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useSessions } from "@/hooks/useSessions";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";
import { GS_SEEN_KEY } from "@/lib/getStarted";

/****************
 * A NEW ACCOUNT STARTS ON GET STARTED. 2026-10-21, engagement redesign.
 *
 * Once per account, on this browser: an account with no session yet that
 * lands on the Dashboard is taken to Get Started. It replaces the automatic
 * video and tour (both still in the header). Never for an admin viewing
 * someone else, never away from a page the person opened on purpose.
 ****************/
export function GetStartedGate() {
  const { user, isLoaded } = useUser();
  const { data: sessions, isSuccess } = useSessions();
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !isLoaded || !user || !isSuccess || isViewingAsSomeoneElse()) return;
    if (location.pathname !== "/") return;
    done.current = true;
    const key = GS_SEEN_KEY(onboardingIdFor(user.id)); // a newcomer copy during a speed run (lib/speedRun.ts)
    let seen = false;
    try { seen = localStorage.getItem(key) === "1"; } catch { seen = true; }
    if (seen || (sessions?.length ?? 0) > 0) return;
    /* The welcome by intent (Mishka, on for every new account 10-22) owns a new
       account's first screen when it claims it; Get Started then stays one click
       away instead of taking over too (merge of 2026-10-22, lib/onboarding.ts). */
    /* Get Started runs on the real Lite pages (Guillaume, 10-31): its guide,
       not a page of its own. */
    const go = () => {
      try { localStorage.setItem(key, "1"); } catch { /* shown once anyway */ }
      startIntentGuide('first_run');
    };
    if (!onboardingEnabled()) { go(); return; }
    return whenFirstVisitDecided((claimed) => { if (!claimed) go(); });
  }, [isLoaded, user, isSuccess, sessions, location.pathname, navigate]);

  return null;
}
